import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { VolumeMuteFill, VolumeUpFill } from 'react-bootstrap-icons';

import Avatar from './avatar/avatar';

const isSafari = () => {
	const user_agent = navigator.userAgent.toLowerCase();
	return user_agent.indexOf('safari') > -1 && user_agent.indexOf('chrome') < 0;
};

const Video = ({
	audio = false,
	avatarSrc,
	avatarSubtitle,
	avatarTitle,
	children,
	className,
	poster,
	progressBar = false,
	rounded_corners,
	title,
	variant = 'responsive',
	video_mp4,
	video_webm,
}) => {
	const reference = useRef(null);
	const [isMuted, setIsMuted] = useState(true);
	const [progress, setProgress] = useState(0);

	const toggleMute = () => {
		if (reference.current) {
			reference.current.muted = !isMuted;
			setIsMuted(!isMuted);
		}
	};

	useEffect(() => {
		const player = reference.current;

		const updateProgress = () => {
			if (player && player.duration) {
				const progressValue = (player.currentTime / player.duration) * 100;
				setProgress(progressValue);
			}
		};

		if (player) {
			player.addEventListener('timeupdate', updateProgress);

			if (isSafari()) {
				player.autoplay = true;
				player.controls = false;
				player.muted = isMuted;
				player.playsinline = true;
				player.setAttribute('muted', '');

				setTimeout(() => {
					const promise = player.play();
					if (promise.then) {
						promise
							.then(() => {})
							.catch(() => {
								player.style.display = 'none';
							});
					}
				}, 0);
			}
		}

		return () => {
			if (player) {
				player.removeEventListener('timeupdate', updateProgress);
			}
		};
	}, [isMuted]);

	const getVariantClass = () => {
		switch (variant) {
			case 'square':
				return 'video__container-square';
			case '16:9':
				return 'video__container-16-9';
			case '9:16':
				return 'video__container-9-16';
			case 'full_width':
				return 'video__container-full-width';
			case 'bioage':
				return 'video__container-bioage';
			default:
				return 'video__container-responsive';
		}
	};

	return (
		<section className="video">
			<div
				className={`video__container o-hidden ${rounded_corners ? 'rounded-3' : ''} ${className}`}
			>
				<div className={getVariantClass()}>
					{(progressBar || avatarSrc) && (
						<div className="video__header">
							{progressBar && (
								<div className="video__progress-bar">
									<div
										className="video__progress-bar-filled"
										style={{ width: `${progress}%` }}
									></div>
								</div>
							)}
							{avatarSrc && (
								<Avatar
									size="sm"
									src={avatarSrc}
									subtitle={avatarSubtitle}
									title={avatarTitle}
								/>
							)}
						</div>
					)}
					<video
						autoPlay={true}
						className={`video__container-video ${rounded_corners ? 'rounded-3' : ''}`}
						loop={true}
						muted={isMuted}
						playsInline={true}
						poster={poster}
						ref={reference}
						title={title}
					>
						{video_mp4 && <source src={video_mp4} type="video/mp4" />}
						{video_webm && <source src={video_webm} type="video/webm" />}
					</video>
					{audio && (
						<button
							className="video__mute-button btn-circle btn btn-dark"
							onClick={toggleMute}
						>
							{isMuted ? <VolumeMuteFill /> : <VolumeUpFill />}
						</button>
					)}
					<canvas className="video__container--canvas d-none"></canvas>
					{children && (
						<div className="video__container--canvas-cover">{children}</div>
					)}
				</div>
			</div>
		</section>
	);
};

Video.propTypes = {
	audio: PropTypes.bool,
	avatarSrc: PropTypes.string,
	avatarSubtitle: PropTypes.string,
	avatarTitle: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	className: PropTypes.string,
	poster: PropTypes.string,
	progressBar: PropTypes.bool,
	rounded_corners: PropTypes.bool,
	title: PropTypes.string,
	variant: PropTypes.oneOf(['responsive', 'square', '16:9', '9:16']),
	video_mp4: PropTypes.string,
	video_webm: PropTypes.string,
};

export default Video;
